import type { CollectionCarousel } from '@tn/foundation/graphql/__generated/gql/graphql';
import { Config } from '@tn/foundation/graphql/__generated/resolvers-types';

import { CarouselModel } from '../EnumCarouselModels';
import { CarouselPresentationStyle } from '../EnumCarouselPresentationStyle';
import { CarouselType } from '../EnumCarouselsTypes';

const TOP_MARQUEE_CAROUSEL_TYPES = new Set<string>([
    CarouselType.SPOTLIGHT,
    CarouselType.SPOTLIGHT_MARQUEE,
    CarouselType.SPOTLIGHT_MARQUEE_169,
]);

/** Base TanStack cache key for `useGetCarouselData` rail queries; full key is `carousel-data-<region>`. */
export const CAROUSEL_DATA_BASE_CACHE_KEY = 'carousel-data';

// Covers a full `options.paginationSize.carousel` batch
export const SSR_CAROUSELS_COUNT = 20;
// Fallback cap on the pre-search view when the
// `options.preSearchCarouselsCount` blueprint key is missing.
export const PRE_SEARCH_CAROUSELS_COUNT_DEFAULT = 3;
export const MAX_SSR_ITEMS_PER_ROW = 20;
export const DISPLAY_AD_BANNER_ROWS = 1;

/** Limits items to {@link MAX_SSR_ITEMS_PER_ROW} or custom limit to keep the initial HTML payload small. */
export function sliceToSSRRowLimit<T>(items: T[], limit: number = MAX_SSR_ITEMS_PER_ROW): T[] {
    return items.slice(0, Math.max(0, limit));
}

/** Carousel models that are user-specific: personalised content and My List. */
export const USER_SPECIFIC_MODELS: string[] = [
    CarouselModel.KEEP_WATCHING,
    CarouselModel.RECENT_CHANNELS,
    CarouselModel.WATCHAGAIN,
    CarouselModel.WATCH_LIST_CHANNELS,
];

type SSRFilterableCarousel = {
    isMyList?: boolean | null;
    isMarquee?: boolean | null;
    model?: string | null;
    carouselId?: string | null;
    carouselPresentationStyle?: string | null;
    carouselType?: string | null;
};

/** True when the carousel renders as the fixed-position home hero (spotlight marquee). */
export function isTopMarqueeCarousel(carousel: SSRFilterableCarousel | null | undefined): boolean {
    if (!carousel) {
        return false;
    }

    return (
        carousel.isMarquee === true ||
        (carousel.carouselType != null && TOP_MARQUEE_CAROUSEL_TYPES.has(carousel.carouselType))
    );
}

export const isUserSpecificCarousel = (model?: string | null, isMyList?: boolean | null): boolean =>
    Boolean(isMyList) || (model != null && USER_SPECIFIC_MODELS.includes(model));

/** Either signal present. For raw rows, before {@link getCarouselType} resolves `carouselType`. */
export const isCreativeAdsCarouselAnySignal = (
    model?: string | null,
    carouselPresentationStyle?: string | null,
): boolean =>
    model === CarouselModel.DISPLAY_CREATIVE ||
    carouselPresentationStyle === CarouselPresentationStyle.DISPLAY_AD_BANNER;

/** Both signals present: the only shape that maps to `CarouselType.DISPLAY_AD_BANNER`. */
export const isCreativeAdsCarouselBothSignals = (
    model?: string | null,
    carouselPresentationStyle?: string | null,
): boolean =>
    model === CarouselModel.DISPLAY_CREATIVE &&
    carouselPresentationStyle === CarouselPresentationStyle.DISPLAY_AD_BANNER;

/** Exactly one signal present, i.e. a misconfigured row that no parser can handle. */
export const isCreativeAdsCarouselSingleSignal = (
    model?: string | null,
    carouselPresentationStyle?: string | null,
): boolean =>
    isCreativeAdsCarouselAnySignal(model, carouselPresentationStyle) &&
    !isCreativeAdsCarouselBothSignals(model, carouselPresentationStyle);

export type SSRCarouselSelectOptions = {
    /**
     * Prefetching a user-specific row writes the viewer's own lists into the document, so only a
     * per-request route may opt in. A route that passes `isCacheable` serves one viewer's HTML to
     * the next and must leave this off.
     */
    includeUserSpecific?: boolean;
};

/**
 * Both the prefetch and the render layer must derive `isSsrPrefetched` from the same options, or
 * the render claims data the cache never received. Shared so the two sides cannot drift.
 */
export const HOME_SSR_CAROUSEL_OPTIONS: SSRCarouselSelectOptions = { includeUserSpecific: true };

/**
 * Returns the first {@link SSR_CAROUSELS_COUNT} home collection carousel rows that are
 * safe to prefetch server-side: excludes user-specific models and My List carousels unless
 * `includeUserSpecific` is set, using the same {@link isUserSpecificCarousel} predicate as the
 * render layer so the prefetch set and the SEO-title behaviour stay aligned. Ad banner rows are
 * always excluded: they are fetched client-side
 */
export function selectSSRCarousels<T extends SSRFilterableCarousel>(
    carousels: (T | null)[] | null | undefined,
    { includeUserSpecific = false }: SSRCarouselSelectOptions = {},
): T[] {
    if (!carousels?.length) {
        return [];
    }
    return carousels
        .filter(
            (row): row is T =>
                row != null &&
                (includeUserSpecific || !isUserSpecificCarousel(row?.model, row?.isMyList)) &&
                !isCreativeAdsCarouselAnySignal(row?.model, row?.carouselPresentationStyle),
        )
        .slice(0, SSR_CAROUSELS_COUNT);
}

/*
 * Returns the Set of carouselIds that will be server-side prefetched for the
 * home page (i.e. the first {@link SSR_CAROUSELS_COUNT} non-user-specific rows).
 *
 * Use this to derive `isBot` in the render list so the flag applies only to
 * carousels that actually have prefetched data in the TanStack Query cache —
 * regardless of how many user-specific carousels (KEEP_WATCHING, etc.) sit
 * above them in the full config list and would otherwise skew a fixed-index check.
 *
 * Pass the same options the server-side selection used, or the two disagree about
 * which rows were prefetched.
 */
export function getSSRPrefetchedIds(
    config: (Config | CollectionCarousel | null)[] | null | undefined,
    options?: SSRCarouselSelectOptions,
): Set<string> {
    return new Set(
        selectSSRCarousels(config, options)
            .map((c) => c?.carouselId)
            .filter((id): id is string => id != null),
    );
}

/** Returns true if the given carouselId is in the SSR-prefetched set. */
export function isSSRPrefetched(
    carouselId: string | null | undefined,
    prefetchedIds: Set<string>,
): boolean {
    return carouselId != null && prefetchedIds.has(carouselId);
}
